import { FC, useEffect } from 'react';

import { useSession } from '@eneco/auth';
import RichText from '@eneco/components/RichText/RichText';
import { useCustomerGetCustomerProfile } from '@eneco/dc/hooks';
import {
  Header,
  Layout,
  LoginCard,
  NextButton,
  PersonalDetailsFormFields,
  formatPersonalDetailsBirthdate,
  personalDetailsBirthdateSchema,
  personalDetailsNameFields,
} from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { ServiceGemakFlowPersonalDetailsStepRendering } from '@sitecore/types/ServiceGemakFlowPersonalDetailsStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextServiceGemak } from '../../context';
import { getMergedUserData } from '../../helpers/dc.nonCommodity';

interface FormFields {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  birthdate: string;
  day: string | number;
  month: string | number;
  year: string | number;
}

export const PersonalDetailsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;
  const context = useFlowSelector(state => state.context);
  const customerData = useCustomerGetCustomerProfile().data;
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';

  const { ServiceGemakFlowPersonalDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ServiceGemakFlowPersonalDetailsStep: ServiceGemakFlowPersonalDetailsStepRendering;
  }>();
  const personalDetails = textualData?.personalDetails;

  const formSchema = yup.object({
    salutation: yup.string().required(personalDetails?.salutationRadio?.value?.requiredMessage),
    ...personalDetailsNameFields({
      firstNameRequired: personalDetails?.firstNameFormField?.value.requiredMessage,
      surnameRequired: personalDetails?.surnameFormField?.value.requiredMessage,
    }),
    ...personalDetailsBirthdateSchema({
      day: personalDetails?.dayFormField?.value,
      month: personalDetails?.monthFormField?.value,
      year: personalDetails?.yearFormField?.value,
      birthdate: personalDetails?.birthdateFormField?.value,
      birthdateKind: 'string',
    }),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
    reset,
  } = useForm<FormFields>({
    mode: 'onTouched',
    defaultValues: {
      ...context,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { date } = useFormatter();

  useEffect(() => {
    reset(getMergedUserData(context, customerData));
  }, [context, customerData, reset]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          salutation: formData.salutation,
          firstName: formData.firstName,
          surnamePreposition: formData.surnamePreposition || undefined,
          surname: formData.surname,
          emailAddress: context.emailAddress ?? customerData?.contact?.emailAddress ?? '',
          phoneNumber:
            context.phoneNumber ?? customerData?.contact?.phoneNumber ?? customerData?.contact?.mobilePhoneNumber ?? '',
          birthdate: formatPersonalDetailsBirthdate(date.short, formData),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>

      {!isAuthenticated && <LoginCard textualData={textualData?.login} />}
      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <PersonalDetailsFormFields
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        personalDetails={personalDetails}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
